import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  CardMedia,
  Input,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/mediaUpload";
import useAuth from "../../hooks/useAuth";
import { showError, showToast } from "../../constants/toasts";
import { useEditMyProfileMutation } from "../../service/auth";
import PhoneInput from "react-phone-input-2";

const ManageProfile = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [editProfile] = useEditMyProfileMutation();
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [startDate, setStartDate] = useState<any>(null);
  const [profileImage, setProfileImage] = useState("");
  const [error, setError] = useState(false);

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setImage(imageUrl?.data || "");
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue("countryCode", country?.dialCode);
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: userData?.fullName || "",
      email: userData?.email || "",
      countryCode: "+" + userData?.countryCode || "",
      phone: userData?.phone || "",
    },

    validationSchema: Yup.object({
      name: Yup.string().required("This field is required"),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Enter a valid email address"
        ),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const body = {
        fullName: values?.name || "",
        email: values?.email || "",
        phone: values?.phone || "",
        countryCode: values?.countryCode || "",
        ...(image ? { image } : {}),
      };

      try {
        const response = await editProfile(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Profile updated successfully");

          navigate("/dashboard");
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  useEffect(() => {
    setImage(userData?.image || "");
  }, []);

  return (
    <>
      <div className="main_title">
        <h1>Manage Profiles</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Profile
        </p>
      </div>

      <div className="cards view_page profile_page_tab">
        <div className="table_header">
          <div className="left_s">
            <h2></h2>
          </div>
          {/* <div className="right_s">
            <Button className="btn btn_primary">
              <EditIcon /> Edit Information
            </Button>
          </div> */}
        </div>

        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* 🔹 Upload Image */}
            <div className="control_group w_100">
              <label htmlFor="icon-button-file">Upload Image</label>
              {image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={image} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = null;
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                      <span className="badge">
                        <Tooltip title="This seller earned the Supporter Badge by referring new customers to Bouteek. It reflects their active contribution to the community and strong marketplace engagement.">
                          <VerifiedIcon />
                        </Tooltip>
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>

            {/* 🔹 User Info Fields */}
            <div className="control_group w_33">
              <label htmlFor="ownerName">Owner Name</label>
              <TextField
                id="name"
                name="name"
                placeholder="Enter Name"
                className="text_field"
                fullWidth
                inputProps={{ maxLength: 80 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="email">Email</label>
              <TextField
                id="email"
                name="email"
                disabled
                placeholder="Enter Email"
                fullWidth
                className="text_field"
                value={formik.values.email}
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="phone">Contact Phone</label>
              <PhoneInput
                enableSearch
                value={formik.values.countryCode + formik.values.phone}
                country={"uae"}
                inputClass="phoneInput"
                onChange={(phone, country) => handleChangePhone(phone, country)}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              <p className="err_msg">
                {error && !formik.values.phone?.length
                  ? "Phone number is required"
                  : ""}
              </p>
            </div>

            {/* 🔹 Date of Birth */}
            {/* <div className="control_group w_33">
              <label>Date of Birth</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      placeholder: "Select Date of Birth",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div> */}

            {/* 🔹 Save Button */}
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                variant="contained"
                type="submit"
                onClick={() => setError(true)}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ManageProfile;
